import { useEffect, useState } from 'react';

import { CancelCircleIcon, CheckmarkBadge02Icon, File02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useCandidateApplicationUploadActions } from 'hooks/upload-actions';
import CandidatePage from 'layouts/CandidatePage/CandidatePage';
import { useParams } from 'react-router-dom';

import { Signature } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';
import { Loading } from 'components/Loading/Loading';

import {
  useGetCandidateBrandByTokenQuery,
  useGetCandidateByTokenQuery,
  useGetCandidateCompanyByTokenQuery,
} from 'api/candidates-api';
import {
  useDeclineRenewalConsentByTokenMutation,
  useGetRenewalConsentCheckTypesByTokenQuery,
  useSubmitRenewalConsentByTokenMutation,
} from 'api/employees-api';

import { CustomButton } from '../CandidateApp/components/CustomButton/CustomButton';

export const CandidateRenewalDataRetentionConsent = () => {
  const { token } = useParams();

  const { getUpload, signUpload, createUpload, deleteUpload } =
    useCandidateApplicationUploadActions(token);

  const {
    data: consentCheckTypes,
    error: errorConsentCheckTypes,
    isLoading: isLoadingConsentCheckTypes,
  } = useGetRenewalConsentCheckTypesByTokenQuery(token, { skip: !token });
  const { data: candidate, isLoading: isLoadingCandidate } = useGetCandidateByTokenQuery(token, {
    skip: !token,
  });
  const { data: company, isLoading: isLoadingCompany } = useGetCandidateCompanyByTokenQuery(token, {
    skip: !token,
  });
  const { data: brand, isLoading: isLoadingBrand } = useGetCandidateBrandByTokenQuery(token, {
    skip: !token,
  });

  const [submitRenewalConsent] = useSubmitRenewalConsentByTokenMutation();
  const [declineRenewalConsent] = useDeclineRenewalConsentByTokenMutation();

  const [signature, setSignature] = useState(null);
  const [consentStatus, setConsentStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (errorConsentCheckTypes && errorConsentCheckTypes.status === 404) {
      setConsentStatus('already_consented');
    }
  }, [errorConsentCheckTypes]);

  const handleSubmitConsent = async () => {
    setLoading(true);
    await submitRenewalConsent({ token, data: { signature_upload_id: signature } });
    setConsentStatus('consent');
    setLoading(false);
  };

  const handleDoNotConsent = async () => {
    if (signature) {
      await deleteUpload(signature);
      setSignature(null);
    }

    await declineRenewalConsent(token);
    setConsentStatus('no_consent');
  };

  return (
    <CandidatePage withTopLogo brand={brand} allowAfterSubmission={true}>
      <Loading
        active={
          isLoadingConsentCheckTypes ||
          isLoadingCandidate ||
          isLoadingCompany ||
          isLoadingBrand ||
          loading
        }
      />
      <IconComponent icon={File02Icon} size="xxlarge" />
      <h1 className="title-3 u-margin-y--large">
        {(consentStatus == 'consent' || consentStatus == 'already_consented') && (
          <div className="d-flex align-items-center">
            Check Renewal Consent Complete
            <HugeiconsIcon
              icon={CheckmarkBadge02Icon}
              className="text-green-2 u-margin-left--small"
              size="30"
            />
          </div>
        )}
        {consentStatus == 'no_consent' && (
          <div className="d-flex align-items-center">
            Check Renewal Consent Declined
            <HugeiconsIcon
              icon={CancelCircleIcon}
              className="text-red-2 u-margin-left--small"
              size="30"
            />
          </div>
        )}
        {!consentStatus && <>Check Renewal Consent</>}
      </h1>
      <p>
        {consentStatus == 'consent' &&
          'Your consent has been recorded. Thank you, you can now close this window.'}
        {consentStatus == 'no_consent' &&
          "No problem. You'll be asked to provide your details each time a check is run. You can now close this window."}
        {consentStatus == 'already_consented' &&
          'Your consent has already been recorded. Thank you, you can now close this window.'}
      </p>
      {!consentStatus && (
        <ConsentForm
          brand={brand}
          candidate={candidate}
          company={company}
          consentCheckTypes={consentCheckTypes}
          signature={signature}
          handleSubmitConsent={handleSubmitConsent}
          handleDoNotConsent={handleDoNotConsent}
          setSignature={setSignature}
          getUpload={getUpload}
          signUpload={signUpload}
          createUpload={createUpload}
          deleteUpload={deleteUpload}
        />
      )}
    </CandidatePage>
  );
};

const ConsentForm = ({
  brand,
  candidate,
  company,
  consentCheckTypes,
  signature,
  setSignature,
  handleSubmitConsent,
  handleDoNotConsent,
  getUpload,
  signUpload,
  createUpload,
  deleteUpload,
}) => (
  <>
    <p>
      I {candidate?.name} have read and understood the Candidate Consent Statement, and consent to
      Checkmate Technology, acting on behalf of {company?.name}, conducting the following ongoing
      compliance checks:
    </p>
    <ul className="u-padding" style={{ listStyleType: 'disc' }}>
      {consentCheckTypes?.map(checkType => (
        <li key={checkType.slug}>{checkType.title}</li>
      ))}
    </ul>
    <p>
      I authorise Checkmate Technology and its nominated verification partners to automatically
      re-run the above checks during my engagement with {company?.name}, as required for compliance
      purposes, without requiring separate consent for each instance, for the duration of my
      employment or engagement with {company?.name}, unless I withdraw my consent.
    </p>
    <p className="u-padding-y">
      I authorise Checkmate Technology and its nominated verification partners to collect, verify,
      and disclose my personal information as needed to carry out these checks, and understand that
      the results may be provided to {company?.name}.
    </p>
    <p>
      These checks are conducted for legitimate employment, ongoing engagement, or compliance
      purposes, in accordance with applicable privacy, data protection, and consumer reporting laws.
      All consents are retained for audit and compliance purposes and may be withdrawn at any time,
      subject to legal or contractual limitations.
    </p>

    <Signature
      className="u-margin-top--large"
      modelParams={{ candidate_id: candidate?.id }}
      metadata={{ renewal_consent_signature: consentCheckTypes?.map(checkType => checkType.slug) }}
      signatureId={signature}
      onChange={setSignature}
      getUpload={getUpload}
      signUpload={signUpload}
      createUpload={createUpload}
      deleteUpload={deleteUpload}
    />

    <div className="d-flex u-margin-top--large">
      <CustomButton
        brand={brand}
        type="secondary"
        small
        className="u-margin-right--small"
        onClick={handleDoNotConsent}>
        Do not consent, I'll provide details each time
      </CustomButton>
      <CustomButton brand={brand} small disabled={!signature} onClick={handleSubmitConsent}>
        I Consent
      </CustomButton>
    </div>
  </>
);
